import { queryOptions } from '@tanstack/react-query';
import { getSeries } from '@/actions/statistics';
import type { StatisticsSeries } from '@/types/series';
import { fetchStatistics } from '../../statisticsQuery/fetchStatistics';
import type { Compare, StatisticsResource } from '../../statisticsQuery/types';

const STATISTICS = 'statistics' as const;
const SERIES = 'statistics-series' as const;

const formatSeriesKey = ({ resource }: { resource: StatisticsResource }) => `series-${resource}`;

const statistics = {
  query: <Series extends StatisticsSeries, C extends Compare>({
    resource,
    params,
    fetchMethod = 'GET',
  }: {
    resource: StatisticsResource;
    params: string;
    fetchMethod?: 'GET' | 'POST';
  }) =>
    queryOptions({
      queryFn: () => fetchStatistics<Series, C>(resource, params, fetchMethod),
      queryKey: [STATISTICS, resource, params, fetchMethod] as const,
    }),
  queryCompare: <Series extends StatisticsSeries, C extends Compare>({
    resource,
    params,
    fetchMethod = 'GET',
  }: {
    resource: StatisticsResource;
    params: string;
    fetchMethod?: 'GET' | 'POST';
  }) =>
    queryOptions({
      queryFn: () => fetchStatistics<Series, C>(resource, params, fetchMethod),
      queryKey: [STATISTICS, 'compare', resource, params, fetchMethod] as const,
    }),
  series: {
    areas: () =>
      queryOptions({
        queryFn: () => getSeries('areas'),
        queryKey: [SERIES, 'series-areas'] as const,
      }),
    developers: () =>
      queryOptions({
        queryFn: () => getSeries('developers'),
        queryKey: [SERIES, 'series-developers'] as const,
      }),
    projects: () =>
      queryOptions({
        queryFn: () => getSeries('projects'),
        queryKey: [SERIES, 'series-projects'] as const,
      }),
    byResource: <Series extends StatisticsSeries>({ resource }: { resource: StatisticsResource }) =>
      queryOptions({
        queryFn: () => getSeries<Series>(resource),
        queryKey: [SERIES, formatSeriesKey({ resource })] as const,
      }),
  },
};

export { formatSeriesKey, SERIES as STATISTICS_SERIES_QUERY_KEY, STATISTICS as STATISTICS_QUERY_KEY, statistics };
